import React, { useRef, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import { useGetProductsEnAvantQuery } from "../../utils/api/product/product.api";
import { MeilleursProduitsCard } from "./MeilleursProduitsCard";

const AUTO_SCROLL_INTERVAL_MS = 4500;

function MeilleursProduitsSkeleton() {
  return (
    <div className="meilleurs-produits-scroll">
      {Array(5)
        .fill(0)
        .map((_, i) => (
          <div key={i} className="meilleurs-produits-scroll-item">
            <div className="meilleurs-produits-card meilleurs-produits-card-skeleton">
              <div className="meilleurs-produits-card-inner">
                <div className="meilleurs-produits-card-image-wrapper">
                  <Skeleton height="100%" style={{ minHeight: 200 }} />
                </div>
                <div className="meilleurs-produits-card-content">
                  <Skeleton width="80%" height={22} className="mb-2" />
                  <Skeleton width="50%" height={14} className="mb-2" />
                  <Skeleton count={2} height={12} className="mb-2" />
                  <Skeleton width="40%" height={20} className="mb-3" />
                  <Skeleton height={44} className="mb-2" />
                  <Skeleton height={44} />
                </div>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
}

const BANNER_PAGE = 1;
const BANNER_LIMIT = 10;

/**
 * Bannière d'accueil : bandeau pleine largeur, sans titre.
 * Cartes produits scrollables horizontalement ; chaque carte = 100% hauteur bannière × 60% largeur.
 * Design : image à gauche (~40%), détails à droite (~60%), typo et boutons selon maquette.
 */
function BannerHomepage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { data: produitsEnAvant, isLoading, isError, refetch } =
    useGetProductsEnAvantQuery({
      page: BANNER_PAGE,
      limit: BANNER_LIMIT,
    });

  const results = produitsEnAvant?.results ?? [];
  const hasResults = results.length > 0;

  useEffect(() => {
    if (isLoading || results.length <= 1) return;
    const el = scrollRef.current;
    if (!el) return;

    const step = () => {
      if (!el) return;
          const item = el.querySelector<HTMLElement>(".meilleurs-produits-scroll-item");
          const itemWidth = item?.offsetWidth ?? el.clientWidth;
          const maxScroll = el.scrollWidth - el.clientWidth;
          if (maxScroll <= 0) return;
          const next = el.scrollLeft + itemWidth;
          if (next >= maxScroll - 2) {
            el.scrollTo({ left: 0, behavior: "smooth" });
          } else {
            el.scrollBy({ left: itemWidth, behavior: "smooth" });
          }
        };

    const id = setInterval(step, AUTO_SCROLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isLoading, results.length]);

  return (
    <section id="equipements" className="py-5  bg-white"
      
      aria-label="Produits mis en avant"
    >
       <h2 className="text-[clamp(24px,5vw,38px)] font-extrabold text-center text-[#111827]  ">
        Matériel &amp; équipements
      </h2>
      <div className="section-meilleurs-produits-banner  mt-16">
      <div className="meilleurs-produits-scroll-wrapper">
        {isLoading ? (
          <MeilleursProduitsSkeleton />
        ) : isError ? (
          <div
            className="flex flex-col items-center justify-center text-center py-12 px-6 max-w-[520px] mx-auto"
            role="alert"
          >
            <p className="text-[15px] font-semibold text-[#1a1a1a] mb-2">
              Impossible de charger les produits pour le moment.
            </p>
            <p className="text-[13px] text-[#555] mb-4">
              Vérifiez votre connexion puis réessayez.
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="bg-[#053965] hover:bg-[#042d52] text-white font-bold text-[14px] rounded-full px-6 py-2 transition-colors duration-200"
            >
              Réessayer
            </button>
          </div>
        ) : !hasResults ? (
          <div className="flex items-center justify-center text-center py-12 px-6 max-w-[520px] mx-auto">
            <p className="text-[14px] font-medium text-[#555]">
              Aucun produit mis en avant pour l’instant. Revenez bientôt !
            </p>
          </div>
        ) : (
          <div ref={scrollRef} className="meilleurs-produits-scroll" role="list">
            {results.slice(0, 10).map((item, key) => (
              <div
                key={item?.id ?? key}
                className="meilleurs-produits-scroll-item"
                role="listitem"
              >
                <MeilleursProduitsCard item={item} />
              </div>
            ))}
          </div>
        )}
      </div>
</div>
    </section>
  );
}

export default BannerHomepage;
